import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { makeData, fC, fN, BRANDS, ALL_PAID, ECOM_PLAT } from '@/dashboard/data';
import axios from 'axios';
import { ArrowLeft, Download, Check, Plus, Trash2, Calendar, User, Send, FileText, ChevronDown, ChevronUp, Lock, ShoppingCart, Sparkles, Building2, Leaf, Zap, Store, Star, Eye, Crown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const F = { mono:"'IBM Plex Mono',monospace", head:"'Outfit',sans-serif" };
const ICON_MAP = { ShoppingCart, Sparkles, Building2, Leaf, Zap, Store };

export default function OmniPlanPage() {
  const { user } = useAuth();
  const { isDark, colors: T } = useTheme();
  const AMBER = isDark ? '#FFD166' : '#B45309';
  const navigate = useNavigate();
  const [data] = useState(() => makeData());
  const [brand] = useState(BRANDS[1]);
  const [showDeck, setShowDeck] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeTab, setActiveTab] = useState('plan');
  const [templates, setTemplates] = useState([]);
  const [purchased, setPurchased] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [tasks, setTasks] = useState([
    { id:1, title:'Shift 15% budget from low-ROAS DSP to Blinkit + Zepto', owner:'Media Team', deadline:'2026-02-15', status:'pending', priority:'critical' },
    { id:2, title:'Launch branded protection campaign on Google Ads', owner:'SEM Lead', deadline:'2026-02-10', status:'pending', priority:'critical' },
    { id:3, title:'A+ content refresh for top 3 Amazon SKUs', owner:'Content Team', deadline:'2026-02-20', status:'pending', priority:'high' },
    { id:4, title:'Test TikTok campaign with creator UGC content (€4K/mo)', owner:'Social Lead', deadline:'2026-02-28', status:'pending', priority:'high' },
    { id:5, title:'Mobile UX audit + one-tap payment integration', owner:'Product Team', deadline:'2026-03-15', status:'pending', priority:'high' },
    { id:6, title:'Launch Pinterest shopping catalog (test €2K/mo)', owner:'Media Team', deadline:'2026-03-01', status:'pending', priority:'medium' },
    { id:7, title:'Activate 3-step win-back email sequence', owner:'CRM Lead', deadline:'2026-02-20', status:'pending', priority:'medium' },
    { id:8, title:'SEO sprint: optimize top 30 pages for featured snippets', owner:'SEO Team', deadline:'2026-03-30', status:'pending', priority:'medium' },
  ]);
  const [newTask, setNewTask] = useState({ title:'', owner:'', deadline:'', priority:'medium' });
  const [expandedBS, setExpandedBS] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/templates`)
      .then(r => setTemplates(r.data))
      .catch(e => console.error('Failed to load templates:', e));
    axios.get(`${API}/api/templates/purchased`, { withCredentials: true })
      .then(r => setPurchased(r.data.map(p => p.template_id)))
      .catch(e => console.error('Failed to load purchases:', e));
  }, []); // API is module-level constant

  const isPremiumUser = user?.plan === 'enterprise' || user?.plan === 'bespoke';
  const addTask = () => { if (!newTask.title) return; setTasks(t => [...t, { ...newTask, id: Date.now(), status: 'pending' }]); setNewTask({ title: '', owner: '', deadline: '', priority: 'medium' }); };
  const removeTask = (id) => setTasks(t => t.filter(x => x.id !== id));
  const toggleTask = (id) => setTasks(t => t.map(x => x.id === id ? { ...x, status: x.status === 'done' ? 'pending' : 'done' } : x));
  const shareTask = (task) => {
    const text = `ACTION ITEM\n${task.title}\nOwner: ${task.owner}\nDeadline: ${task.deadline}\nPriority: ${task.priority}\n\n- OmniPlan by OmniView`;
    if (navigator.share) navigator.share({ title: 'OmniPlan Task', text }); else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const purchaseTemplate = async (tid) => {
    setPurchasing(tid);
    try {
      // First check if free (premium user)
      const { data: checkData } = await axios.post(`${API}/api/templates/${tid}/purchase`, {}, { withCredentials: true });
      if (checkData.purchased) {
        setPurchased(p => [...p, tid]);
        setPurchasing(null);
        return;
      }
      // Non-premium: go through Stripe checkout
      const origin = window.location.origin;
      const { data: checkoutData } = await axios.post(`${API}/api/templates/${tid}/checkout`,
        { origin_url: origin }, { withCredentials: true });
      if (checkoutData.url) {
        window.location.href = checkoutData.url;
      }
    } catch (e) { alert(e.response?.data?.detail || 'Purchase error'); }
    setPurchasing(null);
  };

  const k = data.kpis;
  const pCols = { critical: '#FF4444', high: '#FF9F43', medium: '#FFD166', low: '#06D6A0' };

  // Generate slide decks (same as before)
  const slides = [
    { title:'Media Plan', subtitle:`${brand.name} | ${brand.ind}`, type:'cover', content:`Integrated Performance Media Plan\nPrepared by OmniView Intelligence\nBudget: ${fC(k.totalSpend)} | Revenue Target: ${fC(k.totalGMV)}\nBlended ROAS Target: ${k.blendedROAS}x` },
    { title:'At A Glance', type:'kpi', items:[{l:'Total Budget',v:fC(k.totalSpend)},{l:'Revenue Target',v:fC(k.totalGMV)},{l:'ROAS Target',v:`${k.blendedROAS}x`},{l:'Total Orders',v:fN(k.totalOrders)},{l:'Avg SOV',v:`${k.avgSOV}%`},{l:'LTV:CAC',v:`${k.ltvCac}x`},{l:'Pipeline',v:fC(k.pipelineValue)},{l:'NTB Rate',v:`${k.totalNTB}%`}] },
    { title:'Budget Allocation by Channel', type:'table', headers:['Platform','Budget','Revenue','ROAS','Conv.','SOV%'], rows:data.paid.slice(0,8).map(p=>[p.name,fC(p.spend),fC(p.revenue),`${p.roas}x`,fN(p.conversions),`${p.sov}%`]) },
    { title:'Channel Prioritization', type:'bullets', items:['Q-Commerce (Blinkit+Zepto): 34% of urban growth, only 4.2% budget → Increase','Google Shopping: ROAS 5.4x (best-in-class) → Scale aggressively','LinkedIn ABM: SQL rate 31% vs 11% industry avg → Maintain','TikTok: 18-34 CPM €38 vs Meta €110 → New test budget','Pinterest: 2.3x purchase intent for beauty/home → New catalog','Nykaa SOV 34.2%: Category dominant → Defend position'] },
    { title:'Revenue Blind Spots', type:'blindspots', items:data.blindSpots.slice(0,4).map(b=>({title:b.title,impact:fC(b.impact),severity:b.sev,action:b.action,channel:b.ch})) },
    { title:'Strong Spots to Amplify', type:'strongspots', items:data.strongSpots.slice(0,4).map(s=>({title:s.title,metric:s.metric,bench:s.bench,channel:s.ch})) },
    { title:'Monthly Glide Path', type:'table', headers:['Month','Spend','GMV','Revenue','ROAS','Leads'], rows:data.monthly.slice(0,6).map(m=>[m.month,fC(m.spend),fC(m.gmv),fC(m.revenue),`${(m.gmv/m.spend).toFixed(1)}x`,fN(m.leads)]) },
    { title:'CRM & Pipeline Health', type:'kpi', items:[{l:'Pipeline Value',v:fC(data.crm.pipelineValue)},{l:'Avg Deal',v:fC(data.crm.avgDealSize)},{l:'Win Rate',v:`${data.crm.winRate}%`},{l:'Sales Cycle',v:`${data.crm.salesCycle}d`},{l:'CAC',v:fC(data.crm.cac)},{l:'LTV',v:fC(data.crm.ltv)},{l:'MQL→SQL',v:`${data.crm.mqlToSql}%`},{l:'Churn',v:`${data.crm.churn}%`}] },
    { title:'Competitive SOV Analysis', type:'table', headers:['Competitor','SOV %','Position'], rows:data.compSOV.map(c=>[c.name,`${c.sov}%`,c.name==='Your Brand'?'Defend':'Challenge']) },
    { title:'Action Plan & Owners', type:'tasks', items:tasks.filter(t=>t.status!=='done').map(t=>({title:t.title,owner:t.owner,deadline:t.deadline,priority:t.priority})) },
    { title:'Risk & Kill Rules', type:'bullets', items:['If ROAS < 1.0x for 7 consecutive days → Pause campaign','If CAC > 2x target for any channel → Reduce budget 50%','If SOV drops below 15% → Activate brand defense campaign','Monthly review: Reallocate 20% from bottom to top performer','Q-Commerce stockout risk → Maintain 21-day inventory buffer','Creative fatigue threshold: Replace ad sets after 3x frequency'] },
    { title:'Next Steps', type:'cover', content:`1. Approve budget allocation within 48 hours\n2. Connect remaining data sources\n3. Deploy OmniClaw agent for automated optimization\n4. Schedule weekly performance reviews\n\nPowered by OmniView Intelligence\n${brand.name} | ${new Date().toLocaleDateString('en-IN',{month:'long',year:'numeric'})}` },
  ];

  const downloadDeck = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;
      const pdf = new jsPDF('l', 'mm', 'a4');
      for (let i = 0; i < slides.length; i++) {
        setCurrentSlide(i);
        await new Promise(r => setTimeout(r, 200));
        const el = document.getElementById('slide-render');
        if (!el) continue;
        const canvas = await html2canvas(el, { backgroundColor: isDark ? '#07090f' : '#ffffff', scale: 2 });
        if (i > 0) pdf.addPage();
        const w = pdf.internal.pageSize.getWidth();
        const h = (canvas.height * w) / canvas.width;
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, Math.min(h, pdf.internal.pageSize.getHeight()));
      }
      pdf.save(`OmniPlan_${brand.name}_Deck.pdf`);
    } catch (e) { console.error(e); }
  };

  // Generate template-specific slides (using user data)
  const genTemplateSlides = (t) => {
    const templateSlides = [
      { title:t.name, subtitle:t.industry, type:'cover', content:`${t.desc}\n\nAuto-populated with your data\nBenchmark ROAS: ${t.benchmark_roas}\nSuggested Budget: ${t.sample_budget}` },
      { title:'Your Numbers at a Glance', type:'kpi', items:[{l:'Budget',v:fC(k.totalSpend)},{l:'Revenue',v:fC(k.totalGMV)},{l:'ROAS',v:`${k.blendedROAS}x`},{l:'Orders',v:fN(k.totalOrders)},{l:'SOV',v:`${k.avgSOV}%`},{l:'LTV:CAC',v:`${k.ltvCac}x`}] },
      { title:'Recommended Channels', type:'bullets', items:t.channels.map((ch,i)=>`${i+1}. ${ch} — ${['Primary growth driver','Scale channel','Test & learn','Retention focus','SOV defense','Attribution'][i%6]}`) },
      { title:'What\'s Included', type:'bullets', items:t.includes },
    ];
    return templateSlides;
  };

  const RenderSlide = ({ slide, idx, total, watermark }) => {
    const bg = isDark ? '#0d111a' : '#ffffff';
    const tc = isDark ? '#e0e0e0' : '#09090b';
    const mc = isDark ? '#888' : '#52525b';
    const bc = isDark ? 'rgba(255,255,255,.07)' : 'rgba(0,0,0,.08)';
    return (
      <div id={showDeck || previewTemplate ? 'slide-render' : undefined} style={{ background:bg, borderRadius:10, border:`1px solid ${bc}`, padding:'36px 40px', minHeight:380, position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:0, left:0, height:4, background:'linear-gradient(90deg,#002FA7,#00D4FF,#7B61FF)', width:'100%' }}/>
        {watermark && (
          <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', pointerEvents:'none', zIndex:10 }}>
            <div style={{ fontSize:48, fontWeight:900, color:isDark?'rgba(255,255,255,.04)':'rgba(0,0,0,.04)', transform:'rotate(-30deg)', fontFamily:F.head, letterSpacing:'.1em' }}>SAMPLE PREVIEW</div>
          </div>
        )}
        <div style={{ position:'absolute', bottom:12, left:40, fontSize:9, color:mc, fontFamily:F.mono }}>OmniView Intelligence</div>
        <div style={{ position:'absolute', bottom:12, right:40, fontSize:9, color:mc, fontFamily:F.mono }}>Slide {idx+1}/{total}</div>
        {slide.type === 'cover' && (
          <div style={{ display:'flex', flexDirection:'column', justifyContent:'center', minHeight:300 }}>
            <div style={{ fontSize:30, fontWeight:800, color:tc, fontFamily:F.head, lineHeight:1.1, marginBottom:10 }}>{slide.title}</div>
            {slide.subtitle && <div style={{ fontSize:15, color:'#00D4FF', fontFamily:F.mono, fontWeight:600, marginBottom:18 }}>{slide.subtitle}</div>}
            <div style={{ fontSize:14, color:mc, lineHeight:2, whiteSpace:'pre-line' }}>{slide.content}</div>
          </div>
        )}
        {slide.type === 'kpi' && (
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:tc, fontFamily:F.head, marginBottom:18 }}>{slide.title}</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:10 }}>
              {slide.items.map((item,i) => (
                <div key={i} style={{ padding:'12px 14px', background:isDark?'rgba(255,255,255,.03)':'rgba(0,0,0,.02)', borderRadius:8, border:`1px solid ${bc}` }}>
                  <div style={{ fontSize:10, color:mc, fontFamily:F.mono, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:4 }}>{item.l}</div>
                  <div style={{ fontSize:20, fontWeight:800, color:tc, fontFamily:F.mono }}>{item.v}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {slide.type === 'table' && (
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:tc, fontFamily:F.head, marginBottom:14 }}>{slide.title}</div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, fontFamily:F.mono }}>
              <thead><tr>{slide.headers.map(h => <th key={h} style={{ padding:'8px 10px', textAlign:'left', borderBottom:`2px solid ${bc}`, color:mc, fontSize:10, textTransform:'uppercase', fontWeight:700 }}>{h}</th>)}</tr></thead>
              <tbody>{slide.rows.map((row,i) => <tr key={i} style={{ borderBottom:`1px solid ${bc}` }}>{row.map((cell,j) => <td key={j} style={{ padding:'8px 10px', color:j===0?tc:mc, fontWeight:j<=2?600:400 }}>{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        )}
        {slide.type === 'bullets' && (
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:tc, fontFamily:F.head, marginBottom:14 }}>{slide.title}</div>
            {slide.items.map((item,i) => (
              <div key={i} style={{ display:'flex', gap:10, padding:'9px 0', borderBottom:`1px solid ${bc}` }}>
                <span style={{ color:'#00D4FF', fontWeight:700, fontFamily:F.mono, flexShrink:0 }}>{String(i+1).padStart(2,'0')}</span>
                <span style={{ fontSize:13, color:tc, lineHeight:1.5 }}>{item}</span>
              </div>
            ))}
          </div>
        )}
        {slide.type === 'blindspots' && (
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:tc, fontFamily:F.head, marginBottom:14 }}>{slide.title}</div>
            {slide.items.map((item,i) => (
              <div key={i} style={{ padding:'10px', marginBottom:6, borderLeft:`3px solid ${pCols[item.severity]}`, borderRadius:'0 6px 6px 0', background:`${pCols[item.severity]}08` }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:tc }}>{item.title}</span>
                  <span style={{ fontSize:13, color:'#06D6A0', fontFamily:F.mono, fontWeight:700 }}>+{item.impact}/mo</span>
                </div>
                <div style={{ fontSize:11, color:mc }}>{item.channel}</div>
                <div style={{ fontSize:12, color:tc, marginTop:3 }}>Action: {item.action}</div>
              </div>
            ))}
          </div>
        )}
        {slide.type === 'strongspots' && (
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:tc, fontFamily:F.head, marginBottom:14 }}>{slide.title}</div>
            {slide.items.map((item,i) => (
              <div key={i} style={{ padding:'10px', marginBottom:6, borderLeft:'3px solid #06D6A0', borderRadius:'0 6px 6px 0', background:'rgba(6,214,160,.05)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:tc }}>{item.title}</span>
                  <span style={{ fontSize:13, color:'#06D6A0', fontFamily:F.mono, fontWeight:700 }}>{item.metric}</span>
                </div>
                <div style={{ fontSize:11, color:mc }}>{item.channel} | Bench: {item.bench}</div>
              </div>
            ))}
          </div>
        )}
        {slide.type === 'tasks' && (
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:tc, fontFamily:F.head, marginBottom:14 }}>{slide.title}</div>
            {slide.items.map((item,i) => (
              <div key={i} style={{ display:'flex', gap:10, padding:'7px 0', borderBottom:`1px solid ${bc}`, alignItems:'center' }}>
                <span style={{ width:6, height:6, borderRadius:'50%', background:pCols[item.priority], flexShrink:0 }}/>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, color:tc, fontWeight:600 }}>{item.title}</div>
                  <div style={{ fontSize:10, color:mc, fontFamily:F.mono }}>{item.owner} | {item.deadline}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`min-h-screen ${isDark?'bg-zinc-950':'bg-zinc-50'}`} style={{ fontFamily:F.head }}>
      {/* Header */}
      <div style={{ background:isDark?'rgba(7,9,15,.98)':'rgba(255,255,255,.95)', borderBottom:`1px solid ${T.border}`,
        padding:'12px 16px', display:'flex', alignItems:'center', gap:10, position:'sticky', top:0, zIndex:100, backdropFilter:'blur(12px)' }}>
        <button data-testid="omniplan-back" onClick={() => navigate('/dashboard')} style={{ display:'flex', alignItems:'center', gap:4, background:'none', border:'none', color:T.muted, cursor:'pointer', fontSize:13 }}>
          <ArrowLeft size={16}/> Dashboard
        </button>
        <div style={{ flex:1 }}/>
        <FileText size={18} style={{ color: AMBER }}/>
        <span style={{ fontSize:16, fontWeight:800 }}>OmniPlan</span>
        <div style={{ flex:1 }}/>
        <button data-testid="open-deck" onClick={() => { setActiveTab('plan'); setShowDeck(true); }}
          style={{ padding:'8px 14px', borderRadius:6, background:'linear-gradient(135deg,#002FA7,#00D4FF)', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', border:'none', display:'flex', alignItems:'center', gap:5 }}>
          <Download size={14}/> Deck
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ borderBottom:`1px solid ${T.border}`, background:isDark?T.bg:'#fff', display:'flex', gap:0 }}>
        {[{id:'plan',label:'My Plan'},{id:'templates',label:'Templates Marketplace'}].map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
            style={{ flex:1, padding:'12px 16px', border:'none', background:'transparent', cursor:'pointer', fontSize:14, fontWeight:activeTab===tab.id?700:500,
              color:activeTab===tab.id?T.text:T.muted, borderBottom:activeTab===tab.id?`2px solid ${AMBER}`:'2px solid transparent', fontFamily:F.head }}>
            {tab.label} {tab.id==='templates' && <span style={{ fontSize:10, background:'#FF6B6B', color:'#fff', padding:'1px 6px', borderRadius:10, marginLeft:4, fontWeight:700 }}>NEW</span>}
          </button>
        ))}
      </div>

      <div style={{ maxWidth:1000, margin:'0 auto', padding:'20px 16px 100px' }}>

        {/* ═══════ MY PLAN TAB ═══════ */}
        {activeTab === 'plan' && (
          <>
            {/* Plan Summary */}
            <div style={{ background:isDark?'#0d111a':'#fff', border:`1px solid ${T.border}`, borderRadius:12, padding:'20px 24px', marginBottom:20 }}>
              <div style={{ fontSize:18, fontWeight:800, marginBottom:4 }}>{brand.name} — Media Plan</div>
              <div style={{ fontSize:13, color:T.muted, fontFamily:F.mono, marginBottom:16 }}>{brand.ind} | Budget: {fC(k.totalSpend)} | Target: {fC(k.totalGMV)} | ROAS: {k.blendedROAS}x</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))', gap:8 }}>
                {[{l:'Budget',v:fC(k.totalSpend),c:'#FF6B6B'},{l:'Revenue',v:fC(k.totalGMV),c:'#06D6A0'},{l:'ROAS',v:`${k.blendedROAS}x`,c:'#00D4FF'},
                  {l:'Orders',v:fN(k.totalOrders),c:'#C77DFF'},{l:'SOV',v:`${k.avgSOV}%`,c:'#FFD166'},{l:'LTV:CAC',v:`${k.ltvCac}x`,c:'#4285F4'}
                ].map(m => (
                  <div key={m.l} style={{ padding:'10px 12px', borderRadius:6, borderTop:`2px solid ${m.c}`, background:isDark?'rgba(255,255,255,.02)':'rgba(0,0,0,.02)' }}>
                    <div style={{ fontSize:10, color:T.muted, fontFamily:F.mono, textTransform:'uppercase' }}>{m.l}</div>
                    <div style={{ fontSize:20, fontWeight:800, fontFamily:F.mono, color:T.text }}>{m.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Blind Spots expandable */}
            <div style={{ background:isDark?'#0d111a':'#fff', border:`1px solid ${T.border}`, borderRadius:12, padding:'20px 24px', marginBottom:20 }}>
              <div style={{ fontSize:15, fontWeight:800, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ width:3, height:18, borderRadius:2, background:'#FF4444' }}/>
                Revenue Blind Spots — Click to Expand
              </div>
              {data.blindSpots.map(b => (
                <div key={b.id} data-testid={`expandable-bs-${b.id}`}>
                  <div onClick={() => setExpandedBS(expandedBS===b.id?null:b.id)}
                    style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', marginBottom:4, cursor:'pointer',
                      borderRadius:6, background:`${pCols[b.sev]}06`, border:`1px solid ${pCols[b.sev]}18` }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background:pCols[b.sev], flexShrink:0 }}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{b.title}</div>
                      <div style={{ fontSize:11, color:T.muted, fontFamily:F.mono }}>{b.ch} | +{fC(b.impact)}/mo</div>
                    </div>
                    {expandedBS===b.id ? <ChevronUp size={16} color={T.muted}/> : <ChevronDown size={16} color={T.muted}/>}
                  </div>
                  {expandedBS===b.id && (
                    <div className="animate-fadeUp" style={{ padding:'12px 16px', marginBottom:8, marginLeft:16, borderLeft:`2px solid ${pCols[b.sev]}`, background:isDark?'rgba(255,255,255,.02)':'rgba(0,0,0,.015)', borderRadius:'0 8px 8px 0' }}>
                      <p style={{ fontSize:13, color:T.textSecondary, lineHeight:1.6, marginBottom:8 }}>{b.desc}</p>
                      <div style={{ fontSize:12, color:'#00D4FF', fontWeight:600, marginBottom:8 }}>Action: {b.action}</div>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                        <button onClick={() => shareTask({ title:b.title, owner:'Assign', deadline:'TBD', priority:b.sev })}
                          style={{ padding:'5px 10px', borderRadius:4, border:`1px solid ${T.border}`, background:T.surfaceAlpha, color:T.muted, fontSize:11, cursor:'pointer', fontFamily:F.mono, display:'flex', alignItems:'center', gap:4 }}>
                          <Send size={11}/> Share
                        </button>
                        <button onClick={() => setTasks(t=>[...t,{id:Date.now(),title:b.action,owner:'Assign',deadline:'TBD',status:'pending',priority:b.sev}])}
                          style={{ padding:'5px 10px', borderRadius:4, border:'1px solid rgba(0,212,255,.3)', background:'rgba(0,212,255,.06)', color:'#00D4FF', fontSize:11, cursor:'pointer', fontFamily:F.mono, display:'flex', alignItems:'center', gap:4 }}>
                          <Plus size={11}/> Add to Tasks
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tasks */}
            <div style={{ background:isDark?'#0d111a':'#fff', border:`1px solid ${T.border}`, borderRadius:12, padding:'20px 24px', marginBottom:20 }}>
              <div style={{ fontSize:15, fontWeight:800, marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ width:3, height:18, borderRadius:2, background:'#00D4FF' }}/>
                Action Items & Tasks ({tasks.filter(t=>t.status!=='done').length} pending)
              </div>
              {tasks.map(t => (
                <div key={t.id} data-testid={`task-${t.id}`}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', marginBottom:4, borderRadius:6,
                    background:t.status==='done'?'rgba(6,214,160,.04)':T.surfaceAlpha, border:`1px solid ${t.status==='done'?'rgba(6,214,160,.15)':T.border}`,
                    opacity:t.status==='done'?0.6:1 }}>
                  <button onClick={() => toggleTask(t.id)} style={{ width:20, height:20, borderRadius:4, border:`2px solid ${t.status==='done'?'#06D6A0':pCols[t.priority]}`,
                    background:t.status==='done'?'#06D6A0':'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {t.status==='done' && <Check size={12} color="#fff"/>}
                  </button>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:T.text, textDecoration:t.status==='done'?'line-through':'none' }}>{t.title}</div>
                    <div style={{ fontSize:11, color:T.muted, fontFamily:F.mono, display:'flex', gap:8, flexWrap:'wrap', marginTop:2 }}>
                      <span style={{ display:'flex', alignItems:'center', gap:3 }}><User size={10}/> {t.owner}</span>
                      <span style={{ display:'flex', alignItems:'center', gap:3 }}><Calendar size={10}/> {t.deadline}</span>
                      <span style={{ color:pCols[t.priority], fontWeight:600 }}>{t.priority}</span>
                    </div>
                  </div>
                  <button onClick={() => shareTask(t)} style={{ padding:4, borderRadius:4, border:`1px solid ${T.border}`, background:'none', color:T.muted, cursor:'pointer' }}><Send size={13}/></button>
                  <button onClick={() => removeTask(t.id)} style={{ padding:4, borderRadius:4, border:`1px solid ${T.border}`, background:'none', color:'#FF6B6B', cursor:'pointer' }}><Trash2 size={13}/></button>
                </div>
              ))}
              <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
                <input data-testid="new-task-title" value={newTask.title} onChange={e=>setNewTask(n=>({...n,title:e.target.value}))} placeholder="New task..."
                  style={{ flex:2, minWidth:140, padding:'8px 10px', borderRadius:5, border:`1px solid ${T.border}`, background:isDark?'#0d111a':'#fff', color:T.text, fontSize:12, fontFamily:F.mono, outline:'none' }}/>
                <input value={newTask.owner} onChange={e=>setNewTask(n=>({...n,owner:e.target.value}))} placeholder="Owner"
                  style={{ flex:1, minWidth:80, padding:'8px 10px', borderRadius:5, border:`1px solid ${T.border}`, background:isDark?'#0d111a':'#fff', color:T.text, fontSize:12, fontFamily:F.mono, outline:'none' }}/>
                <input type="date" value={newTask.deadline} onChange={e=>setNewTask(n=>({...n,deadline:e.target.value}))}
                  style={{ padding:'8px 10px', borderRadius:5, border:`1px solid ${T.border}`, background:isDark?'#0d111a':'#fff', color:T.text, fontSize:12, fontFamily:F.mono, outline:'none' }}/>
                <select value={newTask.priority} onChange={e=>setNewTask(n=>({...n,priority:e.target.value}))}
                  style={{ padding:'8px', borderRadius:5, border:`1px solid ${T.border}`, background:isDark?'#0d111a':'#fff', color:T.text, fontSize:12, fontFamily:F.mono }}>
                  <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
                <button data-testid="add-task-btn" onClick={addTask}
                  style={{ padding:'8px 14px', borderRadius:5, background:'#00D4FF', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', border:'none', display:'flex', alignItems:'center', gap:4 }}>
                  <Plus size={13}/> Add
                </button>
              </div>
            </div>
          </>
        )}

        {/* ═══════ TEMPLATES MARKETPLACE TAB ═══════ */}
        {activeTab === 'templates' && (
          <>
            <div style={{ textAlign:'center', marginBottom:24 }}>
              <h2 style={{ fontSize:24, fontWeight:800, marginBottom:6 }}>Industry Templates</h2>
              <p style={{ fontSize:14, color:T.muted, maxWidth:500, margin:'0 auto', lineHeight:1.6 }}>
                Pre-built media plans auto-populated with your actual data. {isPremiumUser ? (
                  <span style={{ color:'#06D6A0', fontWeight:700 }}>Free for Enterprise & Bespoke plans</span>
                ) : 'Free preview (4 slides), full access from €999.'}
              </p>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))', gap:14 }}>
              {templates.map(t => {
                const Icon = ICON_MAP[t.icon] || FileText;
                const owned = purchased.includes(t.id) || isPremiumUser;
                return (
                  <div key={t.id} data-testid={`template-${t.id}`}
                    style={{ background:isDark?'#0d111a':'#fff', border:`1px solid ${T.border}`, borderRadius:12, overflow:'hidden', transition:'all .2s' }}
                    onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow=`0 8px 24px ${t.color}15`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}>
                    <div style={{ height:4, background:t.color }}/>
                    <div style={{ padding:'18px 20px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                        <div style={{ width:40, height:40, borderRadius:8, background:`${t.color}15`, border:`1px solid ${t.color}30`,
                          display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <Icon size={20} style={{ color:t.color }}/>
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:15, fontWeight:800, color:T.text }}>{t.name}</div>
                          <div style={{ fontSize:11, color:T.muted, fontFamily:F.mono }}>{t.industry}</div>
                        </div>
                        {t.featured && <Star size={14} style={{ color: AMBER }}/>}
                      </div>

                      <p style={{ fontSize:12, color:T.textSecondary, lineHeight:1.6, marginBottom:12, minHeight:50 }}>{t.desc}</p>

                      <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginBottom:12 }}>
                        {t.includes.slice(0,3).map(inc => (
                          <span key={inc} style={{ fontSize:9, padding:'2px 6px', borderRadius:3, background:`${t.color}10`, border:`1px solid ${t.color}25`, color:t.color, fontFamily:F.mono, fontWeight:500 }}>{inc}</span>
                        ))}
                        {t.includes.length > 3 && <span style={{ fontSize:9, color:T.muted, fontFamily:F.mono }}>+{t.includes.length-3} more</span>}
                      </div>

                      <div style={{ display:'flex', gap:6, marginBottom:12 }}>
                        <div style={{ fontSize:10, color:T.muted, fontFamily:F.mono }}>Channels: {t.channels.length}</div>
                        <div style={{ fontSize:10, color:T.muted, fontFamily:F.mono }}>Slides: {t.slides}</div>
                        <div style={{ fontSize:10, color:'#06D6A0', fontFamily:F.mono }}>ROAS: {t.benchmark_roas}</div>
                      </div>

                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        {/* Price */}
                        <div style={{ flex:1 }}>
                          {owned ? (
                            <span style={{ fontSize:13, color:'#06D6A0', fontWeight:700, fontFamily:F.mono, display:'flex', alignItems:'center', gap:4 }}><Check size={14}/> Owned</span>
                          ) : isPremiumUser ? (
                            <span style={{ fontSize:13, color:'#06D6A0', fontWeight:700, fontFamily:F.mono }}>FREE</span>
                          ) : (
                            <span style={{ fontSize:18, fontWeight:800, color:T.text, fontFamily:F.mono }}>€{t.price.toLocaleString()}</span>
                          )}
                        </div>

                        {/* Preview button */}
                        <button data-testid={`preview-${t.id}`} onClick={() => setPreviewTemplate(t)}
                          style={{ padding:'7px 12px', borderRadius:5, border:`1px solid ${t.color}40`, background:'transparent', color:t.color,
                            fontSize:11, cursor:'pointer', fontFamily:F.mono, fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
                          <Eye size={12}/> Preview
                        </button>

                        {/* Buy / Get */}
                        {!owned && (
                          <button data-testid={`buy-${t.id}`} onClick={() => purchaseTemplate(t.id)} disabled={purchasing===t.id}
                            style={{ padding:'7px 14px', borderRadius:5, background:t.color, color:'#fff',
                              fontSize:11, cursor:'pointer', fontFamily:F.mono, fontWeight:700, border:'none', display:'flex', alignItems:'center', gap:4, opacity:purchasing===t.id?0.6:1 }}>
                            {isPremiumUser ? <Crown size={12}/> : <ShoppingCart size={12}/>}
                            {isPremiumUser ? 'Get Free' : `Buy €${t.price.toLocaleString()}`}
                          </button>
                        )}
                        {owned && (
                          <button data-testid={`use-${t.id}`} onClick={() => { setPreviewTemplate(t); }}
                            style={{ padding:'7px 14px', borderRadius:5, background:t.color, color:'#fff',
                              fontSize:11, cursor:'pointer', fontFamily:F.mono, fontWeight:700, border:'none', display:'flex', alignItems:'center', gap:4 }}>
                            <Download size={12}/> Use Template
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ═══════ DECK VIEWER MODAL ═══════ */}
      {showDeck && (
        <div style={{ position:'fixed', inset:0, background:isDark?'rgba(0,0,0,.85)':'rgba(0,0,0,.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(8px)' }}
          onClick={() => setShowDeck(false)}>
          <div onClick={e=>e.stopPropagation()} style={{ width:'min(900px,95vw)', maxHeight:'90vh', overflow:'auto', borderRadius:16, background:isDark?'#0a0d14':'#f8f8f8', border:`1px solid ${T.border}` }}>
            <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:10, position:'sticky', top:0, background:isDark?'#0a0d14':'#f8f8f8', zIndex:2 }}>
              <span style={{ fontSize:14, fontWeight:800 }}>Deck — {slides.length} Slides</span>
              <div style={{ flex:1 }}/>
              <button onClick={() => setCurrentSlide(Math.max(0,currentSlide-1))} disabled={currentSlide===0} style={{ padding:'4px 10px', borderRadius:4, border:`1px solid ${T.border}`, background:T.surfaceAlpha, color:T.muted, cursor:'pointer', fontSize:11 }}>Prev</button>
              <span style={{ fontSize:11, color:T.muted, fontFamily:F.mono }}>{currentSlide+1}/{slides.length}</span>
              <button onClick={() => setCurrentSlide(Math.min(slides.length-1,currentSlide+1))} disabled={currentSlide===slides.length-1} style={{ padding:'4px 10px', borderRadius:4, border:`1px solid ${T.border}`, background:T.surfaceAlpha, color:T.muted, cursor:'pointer', fontSize:11 }}>Next</button>
              <button data-testid="download-deck" onClick={downloadDeck} style={{ padding:'6px 14px', borderRadius:5, background:'linear-gradient(135deg,#002FA7,#00D4FF)', color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', border:'none', display:'flex', alignItems:'center', gap:4 }}>
                <Download size={13}/> PDF
              </button>
              <button onClick={() => setShowDeck(false)} style={{ padding:'4px 8px', borderRadius:4, border:`1px solid ${T.border}`, background:T.surfaceAlpha, color:T.muted, cursor:'pointer', fontSize:16 }}>x</button>
            </div>
            <div style={{ padding:20 }}><RenderSlide slide={slides[currentSlide]} idx={currentSlide} total={slides.length}/></div>
            <div style={{ padding:'10px 20px 16px', display:'flex', gap:5, overflowX:'auto' }}>
              {slides.map((s,i) => (
                <button key={i} onClick={() => setCurrentSlide(i)} style={{ flexShrink:0, padding:'5px 9px', borderRadius:4, fontSize:9, fontFamily:F.mono, cursor:'pointer',
                  background:i===currentSlide?'rgba(0,212,255,.12)':'transparent', border:i===currentSlide?'1px solid rgba(0,212,255,.3)':`1px solid ${T.border}`, color:i===currentSlide?'#00D4FF':T.muted, whiteSpace:'nowrap' }}>
                  {i+1}. {s.title.substring(0,12)}{s.title.length>12?'..':''}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════ TEMPLATE PREVIEW MODAL ═══════ */}
      {previewTemplate && (() => {
        const t = previewTemplate;
        const tSlides = genTemplateSlides(t);
        const owned = purchased.includes(t.id) || isPremiumUser;
        const maxPreview = owned ? tSlides.length : Math.min(4, tSlides.length);
        return (
          <div style={{ position:'fixed', inset:0, background:isDark?'rgba(0,0,0,.85)':'rgba(0,0,0,.6)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(8px)' }}
            onClick={() => setPreviewTemplate(null)}>
            <div onClick={e=>e.stopPropagation()} style={{ width:'min(900px,95vw)', maxHeight:'90vh', overflow:'auto', borderRadius:16, background:isDark?'#0a0d14':'#f8f8f8', border:`1px solid ${T.border}` }}>
              <div style={{ padding:'14px 20px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:10, position:'sticky', top:0, background:isDark?'#0a0d14':'#f8f8f8', zIndex:2 }}>
                <div style={{ width:6, height:6, borderRadius:'50%', background:t.color }}/>
                <span style={{ fontSize:14, fontWeight:800 }}>{t.name}</span>
                {!owned && <span style={{ fontSize:10, color:T.muted, fontFamily:F.mono }}>Sample Preview (4 slides with watermark)</span>}
                {owned && <span style={{ fontSize:10, color:'#06D6A0', fontFamily:F.mono, fontWeight:600 }}>Full Access</span>}
                <div style={{ flex:1 }}/>
                {!owned && (
                  <button onClick={() => { purchaseTemplate(t.id); }} style={{ padding:'6px 14px', borderRadius:5, background:t.color, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', border:'none', display:'flex', alignItems:'center', gap:4 }}>
                    {isPremiumUser ? 'Get Free' : `Buy €${t.price.toLocaleString()}`}
                  </button>
                )}
                <button onClick={() => setPreviewTemplate(null)} style={{ padding:'4px 8px', borderRadius:4, border:`1px solid ${T.border}`, background:T.surfaceAlpha, color:T.muted, cursor:'pointer', fontSize:16 }}>x</button>
              </div>
              <div style={{ padding:20 }}>
                {tSlides.slice(0, maxPreview).map((slide, idx) => (
                  <div key={idx} style={{ marginBottom:16 }}>
                    <RenderSlide slide={slide} idx={idx} total={t.slides} watermark={!owned}/>
                  </div>
                ))}
                {!owned && (
                  <div style={{ textAlign:'center', padding:'40px 20px', borderRadius:12, border:`2px dashed ${T.border}`, background:isDark?'rgba(255,255,255,.02)':'rgba(0,0,0,.02)' }}>
                    <Lock size={28} color={T.muted} style={{ margin:'0 auto 12px' }}/>
                    <div style={{ fontSize:16, fontWeight:700, marginBottom:6, color:T.text }}>+{t.slides - maxPreview} more slides locked</div>
                    <p style={{ fontSize:13, color:T.muted, marginBottom:16, maxWidth:400, margin:'0 auto 16px' }}>
                      Full template includes {t.includes.join(', ')} — auto-populated with your actual data.
                    </p>
                    <button onClick={() => purchaseTemplate(t.id)}
                      style={{ padding:'10px 24px', borderRadius:6, background:t.color, color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', border:'none' }}>
                      {isPremiumUser ? 'Get Free' : `Unlock for €${t.price.toLocaleString()}`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
